import React, { useEffect } from "react";
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// 泙 IMPORT useAuth to get the user ID
import { useAuth } from './context/AuthContext';

// --- COMPONENTS ---
import Layout from "./components/Layout"; 
import ProtectedRoute from "./components/ProtectedRoute";
import RouteErrorBoundary from "./components/common/RouteErrorBoundary";

// --- PAGES ---
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import Dashboard from "./pages/Dashboard";

import PurchaseList from "./pages/Material/Purchase/PurchaseList";
import CreatePurchaseRequest from './pages/Material/Purchase/CreatePurchaseRequest'; 
import PurchaseOrderDetail from './pages/Material/Purchase/PurchaseOrderDetail';
import PurchaseOrderList from "./pages/Material/Purchase/PurchaseOrderList";
import QuotationManagement from './pages/Material/Purchase/QuotationManagement';
import InternalPickingPage from "./pages/Material/InternalPicking/InternalPickingPage";
import ScrapListPage from "./pages/Material/Scrap/ScrapListPage";
import ScrapForm from "./pages/Material/Scrap/ScrapForm";           
import ScrapReusePage from "./pages/Material/Scrap/ScrapReusePage"; 
import ScrapSalePage from "./pages/Material/Scrap/ScrapSalePage";   
import WarehouseManagementPage from './pages/Inventory/WarehouseManagementPage';
import WarehouseDetailView from './pages/Inventory/WarehouseDetailView';
import Inventory from "./pages/Inventory/InventoryPage";
import InventoryForm from "./pages/Inventory/InventoryForm";
import MaterialIssueForm from "./pages/Inventory/MaterialIssueForm";
import ReceiptForm from "./pages/Inventory/ReceiptForm";
import ProjectsPage from "./pages/Projects/ProjectsPage";
import ProjectDetails from "./pages/Projects/ProjectDetails";
import TaskPage from "./pages/Task/TaskPage";
import TaskForm from "./pages/Task/TaskForm";
import TaskDetails from "./pages/Task/TaskDetails";
import CostSheetDetail from './pages/Costing/CostSheetDetail';
import BudgetPage from "./pages/Budget/BudgetPage";
import BudgetForm from "./pages/Budget/BudgetForm";
import VendorPage from "./pages/Vendor/VendorPage";
import VendorForm from "./pages/Vendor/VendorForm";
import VendorDetails from "./pages/Vendor/VendorDetails";
import VendorPortal from "./pages/Vendor/VendorPortal";
import EmployeePage from "./pages/Employee/EmployeePage";
import EmployeeForm from "./pages/Employee/EmployeeForm";
import EmployeeDetails from "./pages/Employee/EmployeeDetail";
import ProcurementPage from "./pages/Procurement/ProcurementPage";
import ProcurementForm from "./pages/Procurement/ProcurementForm";
import DocumentPage from "./pages/Document/DocumentPage";
import DocumentForm from "./pages/Document/DocumentForm";
import Compliance from "./pages/Compliance/CompliancePage";
import ComplianceForm from "./pages/Compliance/ComplianceForm";
import Reporting from "./pages/Reporting-Analytics";
import VendorBill from "./pages/Material/Purchase/VendorBill";
import InternalPickingForm from './pages/Material/InternalPicking/InternalPickingForm'; 
import InternalPickingList from './pages/Material/InternalPicking/InternalPickingList'; 
import AddRole from "./pages/Admin/RoleManagement/AddRole";
import UserList from "./pages/Admin/UserManagement/UserList";
import AddUser from "./pages/Admin/UserManagement/AddUser";
import EditUser from "./pages/Admin/UserManagement/EditUser";
import RoleList from "./pages/Admin/RoleManagement/RoleList";
import EditRole from "./pages/Admin/RoleManagement/EditRole";
import ViewRole from "./pages/Admin/RoleManagement/ViewRole";
import ViewUser from "./pages/Admin/UserManagement/ViewUser";
import SubscriptionAdminPage from "./pages/Admin/SubscriptionManagement/SubscriptionAdminPage";
import CRM from './pages/CRM';
import CRMDetailPage from './pages/CRMDetailPage';
import { ConsumptionProvider } from "./context/ConsumptionContext";
import WorkforcePage from "./pages/Workforce/WorkforcePage";
import WorkforceForm from "./pages/Workforce/WorkforceForm";
import AssetPage from "./pages/Assets/AssetPage";
import AssetForm from "./pages/Assets/AssetForm";
import AddWorkforce from './pages/Workforce/AddWorkforce';
import AccountingDashboard from "./pages/Accounting/AccountingDashboard"; 
import InvoiceForm from "./pages/Accounting/InvoiceForm";
import InvoiceList from "./pages/Accounting/InvoiceList";
import VendorBillList from "./pages/Accounting/VendorBillList";
import VendorBillForm from "./pages/Accounting/VendorBillForm";
import GeneralLedger from "./pages/Accounting/GeneralLedger";
import BudgetVsActual from "./pages/Budget/BudgetVsActual";
import VendorBillDetail from './pages/Accounting/VendorBillDetail';
import InvoiceDetail from './pages/Accounting/InvoiceDetail';
import SettingsPage from "./pages/Settings/SettingsPage";
import CommunicationPanel from "./pages/CommunicationPanel";
import LanguageController from "./components/common/LanguageController";
import GoogleTranslateFix from "./components/common/GoogleTranslateFix";
import FontLoader from "./components/common/FontLoader";
import TranslationLoader from './components/Translationloader';
import NotificationsPage from "./pages/NotificationsPage";
import GlobalSocketListener from './components/common/GlobalSocketListener';
// --- WORKFORCE / HR PAGES ---
import AttendancePage from "./pages/Workforce/AttendancePage";
import ShiftRosterPage from "./pages/Workforce/ShiftRosterPage";
import LeaveRequestsPage from "./pages/Workforce/LeaveRequestsPage";
import PayrollDashboard from "./pages/Workforce/PayrollDashboard";
import MyPayslips from "./pages/Workforce/MyPayslips";
import AdminLeaveBalances from "./pages/Workforce/AdminLeaveBalances";

// 🟢 CUSTOMER PORTAL
import CustomerPortal from "./pages/Customer/CustomerPortal";

// 🟢 NEW SETTINGS PAGE IMPORTED
import NotificationSettingsPage from './pages/Settings/NotificationSettingsPage';

// 🟢 SUBSCRIPTION & PAYWALL MODALS
import PaywallModal from "./components/subscription/PaywallModal";
import CheckoutModal from "./components/subscription/CheckoutModal";

/**
 * 泙 1. The GlobalWrapper
 */
function GlobalWrapper() {
  const { user } = useAuth(); // 泙 Get logged in user
  const location = useLocation();

  // 🟢 Enforce strict light mode on login/signup and restore user preference inside dashboard
  useEffect(() => {
    const p = (location.pathname || '').toLowerCase();
    const isAuth =
      p === '/login' ||
      p === '/signup' ||
      p === '/forgot-password' ||
      p === '/reset-password' ||
      p.indexOf('/login') !== -1 ||
      p.indexOf('/signup') !== -1 ||
      p.indexOf('/forgot-password') !== -1 ||
      p.indexOf('/reset-password') !== -1;
    if (isAuth) {
      document.documentElement.classList.remove('dark');
    } else {
      try {
        const savedTheme = localStorage.getItem('briq_theme') || 'light';
        const isDark = savedTheme === 'dark' || (savedTheme === 'system' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
        if (isDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      } catch (e) {}
    }
  }, [location.pathname]);

  return (
    <>
      {/* 🟢 Single Unified Global Toast Notification Container */}
      <ToastContainer 
        position="top-right" 
        autoClose={3500} 
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        style={{ zIndex: 99999999 }}
      />

      {/* 泙 Corrected: GlobalSocketListener is now active globally for logged-in users */}
      {user && user.id && <GlobalSocketListener userId={user.id} />}
      
      {/* 🟢 Global Paywall & Gateway Checkout Modals */}
      <PaywallModal />
      <CheckoutModal />

      <div id="google_translate_element"></div>
      <LanguageController />
      <TranslationLoader />
      <FontLoader />
      <GoogleTranslateFix />
      <ConsumptionProvider>
        <Outlet />
      </ConsumptionProvider>
    </>
  );
}

/**
 * 泙 2. The Data Router Definition
 */
const router = createBrowserRouter([
  {
    element: <GlobalWrapper />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/signup", element: <SignupPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/reset-password", element: <ForgotPasswordPage /> },
      {
        element: <Layout />,
        children: [
          { path: "/admin/dashboard", element: <ProtectedRoute requiredPermission="dashboard.view"><Dashboard /></ProtectedRoute> },
          { path: "/admin/customer-portal", element: <ProtectedRoute requiredPermission="customer.portal"><CustomerPortal /></ProtectedRoute> },
          { path: "/customer-portal", element: <ProtectedRoute requiredPermission="customer.portal"><CustomerPortal /></ProtectedRoute> },
          { path: "/notifications", element: <NotificationsPage /> },
          
          // SETTINGS
          { path: "/admin/settings", element: <ProtectedRoute requiredPermission="settings.view"><SettingsPage /></ProtectedRoute> },
          // 🟢 ADDED NOTIFICATION SETTINGS ROUTE HERE
          { path: "/admin/settings/notifications", element: <ProtectedRoute requiredPermission="settings.view"><NotificationSettingsPage /></ProtectedRoute> },
          { 
  path: "/admin/workforce/leave-balances", 
  element: <ProtectedRoute requiredPermission="workforce.edit"><AdminLeaveBalances /></ProtectedRoute> 
},
          // FINANCE & ACCOUNTING
          { path: "/admin/accounting/dashboard", element: <ProtectedRoute requiredPermission="financial.view"><AccountingDashboard /></ProtectedRoute> },
          { path: "/admin/BudgetPage", element: <ProtectedRoute requiredPermission="financial.view"><BudgetPage /></ProtectedRoute> },
          { path: "/admin/BudgetPage/add", element: <ProtectedRoute requiredPermission="financial.create"><BudgetForm /></ProtectedRoute> },
          { path: "/admin/BudgetPage/edit/:id", element: <ProtectedRoute requiredPermission="financial.create"><BudgetForm /></ProtectedRoute> },
          { path: "/admin/accounting/invoices", element: <ProtectedRoute requiredPermission="accounting.invoices"><InvoiceList /></ProtectedRoute> },
          { path: "/admin/accounting/bills", element: <ProtectedRoute requiredPermission="accounting.bills"><VendorBillList /></ProtectedRoute> },
          { path: "/admin/accounting/ledger", element: <ProtectedRoute requiredPermission="accounting.reports"><GeneralLedger /></ProtectedRoute> },
          { path: "/admin/accounting/invoices/new", element: <ProtectedRoute requiredPermission="accounting.invoices"><InvoiceForm /></ProtectedRoute> },
          { path: "/admin/accounting/bills/new", element: <ProtectedRoute requiredPermission="accounting.bills"><VendorBillForm /></ProtectedRoute> },
          { path: "/admin/accounting/bills/:id", element: <VendorBillDetail /> },
          { path: "/admin/accounting/invoices/:id", element: <InvoiceDetail /> },
          { path: "/admin/accounting/reports/budget", element: <ProtectedRoute requiredPermission="accounting.reports"><BudgetVsActual /></ProtectedRoute> },

          // CRM
          { path: "/crm", element: <ProtectedRoute requiredPermission="crm.view"><CRM /></ProtectedRoute> },
          { path: "/crm/detail", element: <ProtectedRoute requiredPermission="crm.view"><CRMDetailPage /></ProtectedRoute> },

          // USER & ROLE
          { path: "/admin/roles", element: <ProtectedRoute requiredPermission="role.view"><RoleList /></ProtectedRoute> },
          { path: "/admin/roles/add", element: <ProtectedRoute requiredPermission="role.create"><AddRole /></ProtectedRoute> },
          { path: "/admin/roles/edit/:id", element: <ProtectedRoute requiredPermission="role.edit"><EditRole /></ProtectedRoute> },
          { path: "/admin/roles/view/:id", element: <ProtectedRoute requiredPermission="role.view"><ViewRole /></ProtectedRoute> },
          { path: "/admin/users", element: <ProtectedRoute requiredPermission="user.view"><UserList /></ProtectedRoute> },
          { path: "/admin/users/add", element: <ProtectedRoute requiredPermission="user.create"><AddUser /></ProtectedRoute> },
          { path: "/admin/users/edit/:id", element: <ProtectedRoute requiredPermission="user.edit"><EditUser /></ProtectedRoute> },
          { path: "/admin/users/view/:id", element: <ProtectedRoute requiredPermission="user.view"><ViewUser /></ProtectedRoute> },
          { path: "/admin/subscriptions", element: <ProtectedRoute requiredPermission="user.view"><SubscriptionAdminPage /></ProtectedRoute> },

          // MATERIAL
          { path: "/admin/material/internal-picking", element: <ProtectedRoute requiredPermission="inventory.picking"><InternalPickingList /></ProtectedRoute> },
          { path: "/admin/material/internal-picking/:id", element: <ProtectedRoute requiredPermission="inventory.picking"><InternalPickingForm /></ProtectedRoute> },
          { path: "/admin/material/purchase", element: <ProtectedRoute requiredPermission="purchase.view"><PurchaseList /></ProtectedRoute> },
          { path: "/admin/material/purchase/new", element: <ProtectedRoute requiredPermission="purchase.view"><CreatePurchaseRequest /></ProtectedRoute> },
          { path: "/admin/material/purchase/edit/:id", element: <ProtectedRoute requiredPermission="purchase.view"><CreatePurchaseRequest /></ProtectedRoute> },
          { path: "/admin/material/purchase-orders", element: <ProtectedRoute requiredPermission="purchase.view"><PurchaseOrderList /></ProtectedRoute> },
          { path: "/admin/material/purchase-orders/:id", element: <ProtectedRoute requiredPermission="purchase.view"><PurchaseOrderDetail /></ProtectedRoute> },
          { path: "/admin/material/purchase/:prId/quotations", element: <ProtectedRoute requiredPermission="purchase.view"><QuotationManagement /></ProtectedRoute> },
          { path: "/admin/material/scrap", element: <ProtectedRoute requiredPermission="inventory.scrap"><ScrapListPage /></ProtectedRoute> },
          { path: "/admin/material/scrap/view/:id", element: <ProtectedRoute requiredPermission="inventory.scrap"><ScrapForm /></ProtectedRoute> },
          { path: "/admin/material/scrap/reuse", element: <ProtectedRoute requiredPermission="inventory.scrap"><ScrapReusePage /></ProtectedRoute> },
          { path: "/admin/material/scrap/sell", element: <ProtectedRoute requiredPermission="inventory.scrap"><ScrapSalePage /></ProtectedRoute> },
          { path: "/admin/material/purchase/bills/new", element: <VendorBill /> },

          // PROJECTS & TASKS
          { path: "/admin/projects", element: <ProtectedRoute requiredPermission="project.view"><ProjectsPage /></ProtectedRoute> },
          { path: "/admin/Projects", element: <ProtectedRoute requiredPermission="project.view"><ProjectsPage /></ProtectedRoute> },
          { path: "/admin/project/:id", element: <ProjectDetails /> },
          { path: "/admin/projects/view/:id", element: <ProjectDetails /> },
          { path: "/admin/Projects/view/:id", element: <ProjectDetails /> },
          { path: "/admin/Task", element: <ProtectedRoute requiredPermission="task.view"><TaskPage /></ProtectedRoute> },
          { path: "/admin/task", element: <ProtectedRoute requiredPermission="task.view"><TaskPage /></ProtectedRoute> },
          { path: "/admin/Task/add", element: <ProtectedRoute requiredPermission="task.edit"><TaskForm /></ProtectedRoute> },
          { path: "/admin/task/add", element: <ProtectedRoute requiredPermission="task.edit"><TaskForm /></ProtectedRoute> },
          { path: "/admin/Task/edit/:id", element: <ProtectedRoute requiredPermission="task.edit"><TaskForm /></ProtectedRoute> },
          { path: "/admin/task/edit/:id", element: <ProtectedRoute requiredPermission="task.edit"><TaskForm /></ProtectedRoute> },
          { path: "/admin/task/view/:id", element: <TaskDetails /> },
          { path: "/admin/Task/view/:id", element: <TaskDetails /> },

          // INVENTORY & ASSETS
          { path: "/admin/inventory", element: <ProtectedRoute requiredPermission="inventory.view"><Inventory /></ProtectedRoute> },
          { path: "/admin/inventory/add", element: <ProtectedRoute requiredPermission="inventory.view"><InventoryForm /></ProtectedRoute> },
          { path: "/admin/inventory/edit/:id", element: <ProtectedRoute requiredPermission="inventory.view"><InventoryForm /></ProtectedRoute> },
          { path: "/admin/inventory/issue", element: <ProtectedRoute requiredPermission="inventory.view"><MaterialIssueForm /></ProtectedRoute> },
          { path: "/admin/warehouse-management", element: <ProtectedRoute requiredPermission="warehouse.view"><WarehouseManagementPage /></ProtectedRoute> },
          { path: "/admin/warehouse-management/:warehouseId", element: <ProtectedRoute requiredPermission="warehouse.view"><WarehouseDetailView /></ProtectedRoute> },
          { path: "/admin/assets", element: <ProtectedRoute requiredPermission="asset.view"><AssetPage /></ProtectedRoute> },
          { path: "/admin/assets/add", element: <ProtectedRoute requiredPermission="asset.view"><AssetForm /></ProtectedRoute> },
          { path: "/admin/assets/edit/:id", element: <ProtectedRoute requiredPermission="asset.view"><AssetForm /></ProtectedRoute> },

          // EMPLOYEE & WORKFORCE
          { path: "/admin/EmployeePage", element: <ProtectedRoute requiredPermission="employee.view"><EmployeePage /></ProtectedRoute> },
          { path: "/admin/EmployeePage/add", element: <ProtectedRoute requiredPermission="employee.view"><EmployeeForm /></ProtectedRoute> },
          { path: "/admin/EmployeePage/edit/:id", element: <ProtectedRoute requiredPermission="employee.view"><EmployeeForm /></ProtectedRoute> },
          { path: "/admin/employee/view/:id", element: <EmployeeDetails /> },
          { path: "/admin/workforce", element: <ProtectedRoute requiredPermission="workforce.view"><WorkforcePage /></ProtectedRoute> },
          { path: "/admin/workforce/add", element: <AddWorkforce /> },
          { path: "/admin/workforce/edit/:id", element: <ProtectedRoute requiredPermission="workforce.view"><WorkforceForm /></ProtectedRoute> },
          // HR & WORKFORCE MODULE
          { 
            path: "/admin/workforce/attendance", 
            element: <ProtectedRoute requiredPermission="workforce.view"><AttendancePage /></ProtectedRoute> 
          },
          { 
            path: "/admin/workforce/shifts", 
            element: <ProtectedRoute requiredPermission="workforce.view"><ShiftRosterPage /></ProtectedRoute> 
          },
          { 
            path: "/admin/workforce/leaves", 
            element: <ProtectedRoute requiredPermission="workforce.view"><LeaveRequestsPage /></ProtectedRoute> 
          },
          { 
            path: "/admin/workforce/payroll", 
            element: <ProtectedRoute requiredPermission="workforce.edit"><PayrollDashboard /></ProtectedRoute> // 泙 ADD THIS LINE
          },
          { 
            path: "/admin/workforce/my-payslips", 
            element: <MyPayslips /> 
          },
          // VENDOR
          { path: "/admin/VendorPage", element: <ProtectedRoute requiredPermission="vendor.view"><VendorPage /></ProtectedRoute> },
          { path: "/admin/VendorPage/add", element: <ProtectedRoute requiredPermission="vendor.view"><VendorForm /></ProtectedRoute> },
          { path: "/admin/VendorPage/edit/:id", element: <ProtectedRoute requiredPermission="vendor.view"><VendorForm /></ProtectedRoute> },
          { path: "/admin/VendorPage/view/:id", element: <VendorDetails /> },
          { path: "/admin/vendor/view/:id", element: <VendorDetails /> },
          { path: "/vendor-portal", element: <VendorPortal /> },

          // OTHERS
          { path: "/admin/procurement", element: <ProtectedRoute requiredPermission="purchase.view"><ProcurementPage /></ProtectedRoute> },
          { path: "/admin/procurement/add", element: <ProtectedRoute requiredPermission="purchase.view"><ProcurementForm /></ProtectedRoute> },
          { path: "/admin/procurement/edit/:id", element: <ProtectedRoute requiredPermission="purchase.view"><ProcurementForm /></ProtectedRoute> },
          { path: "/admin/DocumentPage", element: <ProtectedRoute requiredPermission="document.view"><DocumentPage /></ProtectedRoute> },
          { path: "/admin/DocumentPage/add", element: <ProtectedRoute requiredPermission="document.view"><DocumentForm /></ProtectedRoute> },
          { path: "/admin/DocumentPage/edit/:id", element: <ProtectedRoute requiredPermission="document.view"><DocumentForm /></ProtectedRoute> },
          { path: "/admin/compliance-safety", element: <ProtectedRoute requiredPermission="compliance.view"><Compliance /></ProtectedRoute> },
          { path: "/admin/compliance-safety/add", element: <ProtectedRoute requiredPermission="compliance.view"><ComplianceForm /></ProtectedRoute> },
          { path: "/admin/compliance-safety/edit/:id", element: <ProtectedRoute requiredPermission="compliance.view"><ComplianceForm /></ProtectedRoute> },
          { path: "/admin/Reporting-Analytics", element: <ProtectedRoute requiredPermission="report.view"><Reporting /></ProtectedRoute> },
          { path: "/admin/material/receipts/view", element: <ProtectedRoute requiredPermission="inventory.view"><ReceiptForm /></ProtectedRoute> },
          { path: "/admin/material/vendor-bill", element: <VendorBill /> },
          { path: "/admin/communication", element: <CommunicationPanel /> },
          { path: "/admin/cost-sheet/:id", element: <ProtectedRoute requiredPermission="financial.view"><CostSheetDetail /></ProtectedRoute> },

          // REDIRECTS
          { path: "/admin/Resource", element: <Navigate to="/admin/workforce" replace /> },
          { path: "/admin/Resource/add", element: <Navigate to="/admin/workforce/add" replace /> },
          { path: "/admin/Resource/edit/:id", element: <Navigate to="/admin/workforce/edit/:id" replace /> },
          { path: "/admin/equipment", element: <Navigate to="/admin/assets" replace /> },
          { path: "/admin/equipment/add", element: <Navigate to="/admin/assets/add" replace /> },
          { path: "/admin/equipment/edit/:id", element: <Navigate to="/admin/assets/edit/:id" replace /> },
          { path: "/admin/equipment/manager", element: <Navigate to="/admin/assets" replace /> },
        ]
      },
      { path: "*", element: <Navigate to="/login" replace /> }
    ]
  }
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;